import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8 md:p-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: November 21, 2025</p>

        <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="leading-relaxed">
              We collect information you provide directly to us when you create an account, use our services, or communicate with us. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account information (name, email, phone number, organization details)</li>
              <li>Booking information (dates, times, services, customer details)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Device information (IP address, browser type, device fingerprints for waiver verification)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our booking platform</li>
              <li>Process bookings and payments</li>
              <li>Send booking confirmations, reminders, and updates</li>
              <li>Provide customer support</li>
              <li>Verify digital waiver signatures with device fingerprinting</li>
              <li>Prevent fraud and ensure platform security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Data Sharing and Storage</h2>
            <p className="leading-relaxed">
              <strong>Your data is yours.</strong> We do not sell your customer data to third parties. Each organization gets its own dedicated database. We only share data with:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service providers (Stripe for payments, Twilio for SMS, email providers)</li>
              <li>As required by law or legal process</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Data Security</h2>
            <p className="leading-relaxed">
              We use industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data at rest and in transit</li>
              <li>Secure authentication with JWT tokens</li>
              <li>Regular security audits and monitoring</li>
              <li>Isolated databases for each organization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Waiver Verification Data</h2>
            <p className="leading-relaxed">
              When customers sign digital waivers, we collect device fingerprints, IP addresses, browser information, and signature data. This information is stored securely and used exclusively for legal verification purposes. Access to this sensitive data is logged in our activity log.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Your Rights</h2>
            <p className="leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Cookies and Tracking</h2>
            <p className="leading-relaxed">
              We use cookies and similar tracking technologies to improve your experience and analyze platform usage. You can control cookies through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="font-semibold">hello@noreply.bookingflows.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
